/* @global Copier */
import React, { Component, Fragment } from "react";
import formatDate from "date-fns/format";
import distanceInWordsToNow from "date-fns/distance_in_words_to_now";
import _find from "lodash/find";
import _includes from "lodash/includes";
import _get from "lodash/get";
import cx from "classnames";
//
import {
  intervention,
  getGenderName,
  getOrientationName,
  getEndowmentName,
  getLanguageName,
  getLanguageLevelName,
  getHumanDuration,
  getRsvpText,
  showScore,
} from "../utils";
import Screen from "./ConnectedScreen";
import PremiumAlert from "./PremiumAlert";
import ModelRates from "./ModelRates";
import { bodyTypes } from "../config";
import Button, { ButtonBasic } from "../components/Button";
import Icon from "../components/Icon";
import Map from "../components/Map";
import Score from "../components/Score";
import Alert from "../components/Alert";
import Distance from "./DistanceContainer";
import Tooltip from "../components/Tooltip";

class ModelScreen extends Component {
  state = {
    share: false,
    mapVisible: false,
    videosExpanded: false,
    contactShown: false,
  };

  _onShare = () => this.setState({ share: true });

  _onCopy = (ev) => {
    Copier.copy(`https://smooci.com/escorts/${this.props.model.hash}`);
    this.setState({ share: false });
  };

  _toggleFavorite = (ev) => {
    const { model, isFavorite, onFavorite, onUnfavorite } = this.props;

    ev.stopPropagation();

    if (isFavorite) {
      onUnfavorite(model.id);
    } else {
      onFavorite(model.id);
    }
  };

  _showMap = (ev) => this.setState({ mapVisible: true });

  getLanguages(languages) {
    return (languages || [])
      .filter((lang) => lang.level !== "")
      .map(
        (lang) =>
          `${getLanguageName(lang.code)} (${getLanguageLevelName(lang.level)})`,
      );
  }

  getButtonLabel() {
    const { rate, duration, online, isTooFar } = this.props;

    if (!online) {
      return "Offline";
    }

    if (!rate) {
      return `${getHumanDuration(duration)} not available`;
    }

    if (isTooFar) {
      return "Location too far";
    }

    return `Send request`;
  }

  showVideos = () => {
    this.setState({ videosExpanded: true });
  };

  showContact = () => {
    this.setState({ contactShown: true });
  };

  render() {
    const {
      city,
      rate,
      isPremium,
      model,
      brand,
      services,
      reviews,
      isFavorite,
      isTooFar,
      online,
      outcall,
      bookModel,
      showCarousal,
      goTo,
      reportReview,
      reviewsReported,
      getReviews,
      noMoreReviews,
    } = this.props;

    const { contactShown, share, mapVisible, videosExpanded } = this.state;

    const bodyType = _find(
      bodyTypes,
      (bodyType) => bodyType.id === model.bodyType,
    );

    const isTrans = model.genderId === 2;
    const spokenLanguages = this.getLanguages(model.languages);

    // FIXME: Hard-coded
    const shareUrl = `https://smooci.com/escorts/${model.hash}`;

    const requiresPremium = !!model.acceptPremiumOnly && !isPremium;

    let premiumAlertText = requiresPremium
      ? model.name + " only accepts Premium members. "
      : "";

    premiumAlertText +=
      "See photos / age verification, only available for _Premium members_.";

    return (
      <Screen
        title={model.name}
        isActive
        nextLabel={this.getButtonLabel()}
        nextDisabled={!online || !rate || isTooFar || requiresPremium}
        onNext={(ev) => bookModel(model.id)}
        className="screen--model"
        onClose={false}
      >
        <ImageGrid
          className="u-mb-tiny"
          hash={model.hash}
          images={model.images.slice(0, 6)}
          isPremium={isPremium}
          onImageClick={showCarousal}
        />
        {(model.videos || []).length > 0 && (
          <ol
            className={cx("o-layout o-layout--tiny u-mb-small videos", {
              "videos--expanded": videosExpanded,
            })}
          >
            {(model.videos || []).map(
              ({ id, expired, contentId, thumbnail }) => (
                <li key={String(id)} className="o-layout__item u-1/2">
                  <a
                    className="video u-mb-tiny"
                    onClick={() =>
                      playVideo(
                        contentId,
                        expired && city.video_premium && !isPremium,
                      )
                    }
                  >
                    <i className="icon icon--large icon-play_circle_outline" />
                    <img
                      src={thumbnail}
                      alt={`${model.name} ${model.city.name} Escort Video #${id}`}
                    />
                  </a>
                </li>
              ),
            )}
          </ol>
        )}
        {!videosExpanded && (model.videos || []).length > 2 && (
          <p className="u-tc">
            <ButtonBasic small title="More Videos" onClick={this.showVideos} />
          </p>
        )}
        {!isPremium && (
          <PremiumAlert className="u-mb-small" text={premiumAlertText} />
        )}
        {isPremium && (
          <ToggleFavorite
            className="u-mb-small"
            modelName={model.name}
            isFavorite={isFavorite}
            onClick={this._toggleFavorite}
          />
        )}
        <ShareButton
          className="u-mb-small"
          modelName={model.name}
          isShared={share}
          onShare={this._onShare}
          onCopy={this._onCopy}
          shareUrl={shareUrl}
        />
        <ChatButton
          {...{
            className: "u-mb-small",
            modelId: model.id,
            modelName: model.name,
          }}
        />
        {model.contactId && (
          <ContactButton
            active={contactShown}
            className="u-mb-small"
            id={model.contactId}
            providerName={model.contactProviderName}
            link={model.contactLink}
            onShow={this.showContact}
          />
        )}
        {isPremium && !online && (
          <NotifyButton
            className="u-mb-small"
            modelName={model.name}
            onClick={(ev) => goTo("model-alert")}
          />
        )}
        <Details>
          {!!model.xo && (
            <Detail
              name="XO"
              value={
                <a target="_blank" href={`https://xo.live?creator=${model.xo}`}>
                  @{model.xo}
                </a>
              }
            />
          )}
          {(model.socials || []).map((social) => {
            if (isPremium) {
              return <Detail name={social.name} value={social.publicId} />;
            }

            return (
              <Detail
                name={social.name}
                value={<a href="/premium">Buy premium</a>}
              />
            );
          })}
          {Boolean(model.offlineAt) && !model.online && (
            <Detail
              name="Last online"
              value={distanceInWordsToNow(new Date(model.offlineAt * 1000))}
            />
          )}
          <Detail name="City" value={model.city.name} />
          {(model.areas || []).length > 0 && (
            <Detail
              name="Areas"
              value={model.areas.map((area) => area.name).join(", ")}
            />
          )}
          {showScore(model.ratingsCount, model.score) && (
            <Detail name="Model Rating" value={<Score value={model.score} />} />
          )}
          <Detail
            name="Agency"
            value={brand === undefined ? "Independent" : brand.name}
          />
          <Detail name="Gender" value={getGenderName(model.genderId)} />
          {Boolean(model.bust && model.cup && model.waist && model.hip) && (
            <Detail
              name="Measurements"
              value={`${model.bust} ${model.cup}–${model.waist}–${model.hip}`}
            />
          )}
          {isTrans && (
            <Detail
              name="Endowment"
              value={getEndowmentName(model.endowment)}
            />
          )}
          {isTrans && (
            <Detail
              name="Orientation"
              value={`${getOrientationName(model.orientation)}`}
            />
          )}
          <Detail
            name="Age"
            value={
              <span>
                {model.age} years old
                {model.ageVerified && (
                  <small className="u-verified">
                    {" "}
                    (Verified <Icon name="check" xsmall />)
                  </small>
                )}
              </span>
            }
          />
          <Detail name="Height" value={`${model.height} cm`} />
          <Detail name="Body Type" value={bodyType ? bodyType.name : "-"} />
          {/* {!!model.nationality && model.city.name !== "Bangkok" && (
            <Detail name="Nationality" value={model.nationality.name} />
          )} */}
          {spokenLanguages.length > 0 && (
            <Detail name="Languages" value={spokenLanguages.join(", ")} />
          )}
          {services.length > 0 && (
            <Detail
              name="Likes"
              value={services.map((service) => (
                <Tooltip
                  key={service.id}
                  name={service.name}
                  description={service.description}
                  className={cx("label", { "label--brand": service.paid })}
                />
              ))}
            />
          )}
          {Boolean(model.rsvp) && (
            <Detail name="Response time" value={getRsvpText(model.rsvp)} />
          )}
          {!outcall && !!model.location && (
            <Detail
              name="Incall location"
              value={
                <span>
                  {model.location.area}{" "}
                  <a className="u-link u-small" onClick={this._showMap}>
                    Show map
                  </a>
                </span>
              }
            />
          )}
          <Detail
            name="Distance"
            value={<Distance icon={false} modelId={model.id} />}
          />
          {/* <Detail
            name="Tracking"
            value={model.tracking ? "Available" : "Not available"}
          /> */}
        </Details>
        {!outcall && mapVisible && (
          <Map
            markers={[
              {
                key: model.location.id,
                position: { lat: model.location.lat, lng: model.location.lng },
              },
            ]}
            approx={500}
            options={{ zoom: 15, scrollwheel: false }}
          />
        )}

        <ModelRates city={model.city} />

        {Boolean(model.paymentMethod) && (
          <div className="u-mt">
            <small className="u-muted">Payment method</small>
            <br />
            {model.paymentMethod}
          </div>
        )}
        {Boolean(model.cancellationPolicy) && (
          <div className="u-mt-small">
            <small className="u-muted">Cancellation policy</small>
            <br />
            {model.cancellationPolicy}
          </div>
        )}

        {Boolean(model.description) && Boolean(model.descriptionApproved) && (
          <div className="u-mt">
            <h3 className="u-mb-small">About me</h3>
            <div className="o-box o-box--small o-box--border">
              {model.description}
            </div>
          </div>
        )}

        <h3 className="u-mt">Reviews</h3>
        {!isPremium && (
          <PremiumAlert
            className="u-mt-small"
            text="Reviews are only available for _Premium members_."
          />
        )}
        {isPremium && reviews.length === 0 && (
          <Alert info className="u-mt-small" text="No reviews yet" />
        )}
        {isPremium && (
          <div>
            {reviews.map((review) => (
              <ReviewItem
                key={review.id}
                reported={_includes(reviewsReported, review.id)}
                onReport={(ev) => reportReview(review.id)}
                {...review}
              />
            ))}
            {!noMoreReviews && reviews.length >= 10 && (
              <div className="u-mb">
                <Button
                  full
                  small
                  mod="basic"
                  title="Load more reviews"
                  onClick={getReviews}
                />
              </div>
            )}
            {noMoreReviews && (
              <Alert info className="u-mb" text="No more reviews" />
            )}
          </div>
        )}
      </Screen>
    );
  }
}

function ImageGrid({ className, images, hash, onImageClick, isPremium }) {
  return (
    <ol className={cx("image-grid o-layout o-layout--tiny", className)}>
      {images.map((image, idx) => (
        <li key={image.id} className="o-layout__item u-1/2">
          <img
            // src={`/escorts/${hash}/images/sm/${image.path}`}
            src={`/images/sm/${image.path}`}
            onClick={(ev) => onImageClick(idx)}
          />
          {isPremium && image.verified === 1 && (
            <small className="ribbon-tag ribbon-tag--small">
              Verified Photo
              <Icon name="check" xsmall />
            </small>
          )}
        </li>
      ))}
    </ol>
  );
}

function ReviewItem({
  id,
  createdAt,
  score,
  comments,
  reported,
  incall,
  onReport,
}) {
  return (
    <div className="reviewItem">
      <div className="reviewItem__head u-small u-clearfix">
        <div className="reviewItem__date u-fl">
          {formatDate(createdAt, "MMM YYYY")}{" "}
          {incall === true && (
            <span className="label label--basic u-xsmall">INCALL</span>
          )}
        </div>
        <ul className="o-list-inline u-fr">
          {!reported && (
            <li>
              <span className="u-link" onClick={onReport}>
                Report
              </span>
            </li>
          )}
          <li>
            <Score value={score} />
          </li>
        </ul>
      </div>
      <blockquote className="reviewItem__text">{comments}</blockquote>
    </div>
  );
}

function Details({ children, className }) {
  return (
    <table className="table--details">
      <tbody>{children}</tbody>
    </table>
  );
}

function Detail({ name, value }) {
  return (
    <tr>
      <td>{name}</td>
      <td>{value}</td>
    </tr>
  );
}

function ToggleFavorite({ modelName, isFavorite, ...rest }) {
  return (
    <ButtonBasic
      full
      small
      title={
        <Fragment>
          {isFavorite ? "Remove" : "Add"} {modelName}{" "}
          {isFavorite ? "from" : "to"} your favorites{" "}
          <Icon name={isFavorite ? "close" : "favorite"} small />
        </Fragment>
      }
      {...rest}
    />
  );
}

function ContactButton({ active, className, id, providerName, link, onShow }) {
  if (active) {
    return (
      <ButtonBasic
        {...{
          className,
          href: link,
          full: true,
          small: true,
          title: `${providerName} @${id}`,
        }}
      />
    );
  }

  return (
    <ButtonBasic
      {...{
        className,
        full: true,
        small: true,
        title: "Show contact details",
        onClick: onShow,
      }}
    />
  );
}

function ShareButton({
  className,
  modelName,
  shareUrl,
  isShared,
  onShare,
  onCopy,
}) {
  return (
    <div className={className}>
      {!isShared && (
        <ButtonBasic
          full
          small
          title={
            <span>
              Share {modelName} profile <Icon name="share" small />
            </span>
          }
          onClick={onShare}
        />
      )}
      {isShared && (
        <div className="input-group">
          <input
            type="text"
            className="form__control form__control--small"
            readOnly
            value={shareUrl}
            autoFocus
            onFocus={(ev) => ev.target.select()}
            onClick={(ev) => ev.target.select()}
          />
          <Button small title="Copy" onClick={onCopy} />
        </div>
      )}
    </div>
  );
}

function ChatButton({ className, modelId, modelName, disabled }) {
  return (
    <div className={className}>
      <ButtonBasic
        {...{
          full: true,
          small: true,
          mod: "brand",
          href: `/chats?model=${modelId}`,
        }}
        title={
          <span>
            Chat with {modelName} <Icon name="chat" small />
          </span>
        }
      />
      {disabled && (
        <PremiumAlert
          className="u-mt-small"
          text="Chat only available for _Premium members_"
        />
      )}
    </div>
  );
}

function NotifyButton({ className, modelName, onClick }) {
  return (
    <ButtonBasic
      full
      small
      className={`${className} o-btn--brand`}
      title={
        <span>
          Notify me when {modelName} is online{" "}
          <Icon name="notifications" small />
        </span>
      }
      onClick={onClick}
    />
  );
}

// @connect
import { connect } from "react-redux";
import { bindActionCreators as bindActions } from "redux";
//
import {
  getModelBrand,
  getModelServices,
  getModelReviews,
  getReviewsNoMore,
  isModelOnline,
  isModelFavorite,
  getModelRate,
  getModelDistance,
  getCity,
  isPremiumIn,
} from "../selectors";

const state = (state) => {
  // Prevents re-reselect selectors to be triggered below
  // Return empty: Component won't update (@see shouldComponentUpdate)
  if (!state.models.active) {
    return {};
  }

  const { flags, booking } = state;

  const city = getCity(state);
  const distance = getModelDistance(state);

  return {
    city,
    rate: getModelRate(state),
    duration: booking.duration,
    outcall: booking.outcall,
    brand: getModelBrand(state),
    services: getModelServices(state) || [],
    reviews: getModelReviews(state) || [],
    isFavorite: isModelFavorite(state),
    online: isModelOnline(state),
    isPremium: isPremiumIn(state),
    reviewsReported: flags.reviews,
    noMoreReviews: getReviewsNoMore(state),
    isTooFar: distance > city.booking_distance,
  };
};

import {
  bookModelAction,
  favoriteModelAction,
  unfavoriteModelAction,
} from "../modules/models/actions";
import { getReviewsAction } from "../modules/reviews/actions";
import { showCarousal } from "../modules/modal";
import { reportReviewAction } from "../modules/flags";

const actions = (dispatch) =>
  bindActions(
    {
      bookModel: bookModelAction,
      showCarousal,
      reportReview: reportReviewAction,
      onFavorite: favoriteModelAction,
      onUnfavorite: unfavoriteModelAction,
      getReviews: getReviewsAction,
    },
    dispatch,
  );

export default connect(state, actions)(ModelScreen);
